import type { PageMessage } from '../../../../types/PageMessage';
import type { SupplierGroupMessage } from '../../../../types/SupplierGroupMessage';
import { PaginatedAsyncMultiComboBox, type ComboBoxOption } from '../../../cdl/ComboBox/ComboBox';
import {
    getSupplierGroups as getSupplierGroupsList,
    type GetSupplierGroupsParams as GetSupplierGroupsListParams,
} from '../../api/clients/company.api';
import { getSupplierGroups, type GetSupplierGroupsParams } from '../../api/clients/supplierGroups.api';
import { queries } from '../../api/queryKeys/queries';
import type { AsyncMultiSelectProps } from '../AsyncSelectProps';

interface SupplierGroupMultiSelectProps extends AsyncMultiSelectProps {
    onChange: (supplierGroupIds: string[]) => void;
    spotAllowed?: boolean;
    value: string[];
}

const mapSupplierGroupToOption = (supplierGroup: SupplierGroupMessage): ComboBoxOption => ({
    value: supplierGroup.id,
    label: supplierGroup.name,
});

const getSupplierGroupParams = (searchQuery: string, page: number, spotAllowed?: boolean): GetSupplierGroupsParams => ({
    searchQuery,
    page,
    spotAllowed,
});

const loadSupplierGroupOptions = async (params: GetSupplierGroupsParams): Promise<PageMessage<ComboBoxOption>> => {
    const supplierGroupsPage = await getSupplierGroups(params);

    return {
        ...supplierGroupsPage,
        content: supplierGroupsPage.content.map(mapSupplierGroupToOption),
    };
};

const loadSelectedSupplierGroupOptions = async (
    params: GetSupplierGroupsListParams,
    spotAllowed?: boolean
): Promise<ComboBoxOption[]> => {
    const supplierGroups = await getSupplierGroupsList(params);

    return supplierGroups
        .filter((supplierGroup) => spotAllowed === undefined || supplierGroup.lubesSettings.spotAllowed === spotAllowed)
        .map(mapSupplierGroupToOption);
};

export function SupplierGroupMultiSelect({ value, onChange, spotAllowed, ...rest }: SupplierGroupMultiSelectProps) {
    return (
        <PaginatedAsyncMultiComboBox
            {...rest}
            value={value}
            onChange={onChange}
            optionsQueryKey={(search) =>
                queries.supplierGroups.paginatedComboBoxOptions(getSupplierGroupParams(search, 0, spotAllowed)).queryKey
            }
            optionsQueryFn={({ search, page }) =>
                loadSupplierGroupOptions(getSupplierGroupParams(search, page, spotAllowed))
            }
            selectedOptionsQueryKey={(values) =>
                queries.supplierGroups.selectedComboBoxOptions(values, spotAllowed).queryKey
            }
            selectedOptionsQueryFn={(values) => loadSelectedSupplierGroupOptions({ ids: values }, spotAllowed)}
        />
    );
}
